/* eslint-disable @typescript-eslint/no-unsafe-return */
import { AxiosResponse } from 'axios';

import type {
  QuantityGetDto,
  RemoveStockTransactionAddDto,
  StockTransactionGetDto,
  LocationDto,
  ChangeLocationDto,
  ProductWithQuantitiesGetDto,
  ResultQuantitiesGetDto,
  OrderProductGetDTO,
} from 'svila-common';
import { ChangeStockTransactionDto } from '../../types/ChangeStockTransactionDto';
import { StockTransactionAddDto } from '~/src/types/StockTransactionAddDto';
import { LocationWithQuantitiesDto } from '~/src/scenes/orders/orders/pages/palletDescription/PalletDescriptions';
import { OrderProductWithReservedQuantityGetDto, OrderWithProductsGetDto } from '~/src/types/Misc';

import { API_URL } from '../../utils/constants';

import request from '../request';
import { ReportRequestDto } from '~/src/types/ReportRequestDto';

export default {
  delete: async (transactionId: string): Promise<AxiosResponse<StockTransactionGetDto>> => {
    return request(`${API_URL}/inventory/${transactionId}`, 'delete');
  },

  add: async (values: StockTransactionAddDto): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/add`, 'post', body);
  },

  change: async (values: ChangeStockTransactionDto): Promise<AxiosResponse<ResultQuantitiesGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory`, 'put', body);
  },

  adjust: async (values: StockTransactionAddDto): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/adjust`, 'post', body);
  },

  adjustTo: async (values: StockTransactionAddDto): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/adjust-to`, 'post', body);
  },

  getByGenericId: async (id: string) => {
    return request(`${API_URL}/inventory/get-for-generic/${id}`, 'get');
  },

  getByGenericInWarehouse: async (warehouseId: string, genericProductId: string) => {
    return request(`${API_URL}/inventory/get-for-generic/${warehouseId}/${genericProductId}`, 'get');
  },

  getTotalsByGenericId: async (id: string) => {
    return request(`${API_URL}/inventory/get-totals-generic/${id}`, 'get');
  },

  getTotalsByGenericInWarehouse: async (warehouseId: string, genericProductId: string) => {
    return request(`${API_URL}/inventory/get-totals-generic/${warehouseId}/${genericProductId}`, 'get');
  },

  getQuantitiesGenericId: async (id: string) => {
    return request(`${API_URL}/inventory/get-quantities-for-generic/${id}`, 'get');
  },

  getQuantitiesForGenericInWarehouse: async (warehouseId: string, genericProductId: string) => {
    return request(`${API_URL}/inventory/get-quantities-for-generic/${warehouseId}/${genericProductId}`, 'get');
  },

  getQuantitiesWithFilterGenericId: async (
    id: string,
    values: StockTransactionAddDto,
  ): Promise<AxiosResponse<QuantityGetDto[]>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/get-quantities-for-generic/${id}`, 'post', body);
  },

  getQuantitiesWithFilterByGenericInWarehouse: async (
    warehouseId: string,
    genericProductId: string,
    values: StockTransactionAddDto,
  ): Promise<AxiosResponse<QuantityGetDto[]>> => {
    const body = JSON.stringify(values);
    return request(
      `${API_URL}/inventory/get-quantities-for-generic/${warehouseId}/${genericProductId}`,
      'post',
      body,
    );
  },

  getQuantitiesByLocation: async (
    chamber: string,
    column: string,
    letter: string,
    level: string,
  ): Promise<AxiosResponse<QuantityGetDto[]>> => {
    return request(
      `${API_URL}/inventory/get-quantities-by-location/${chamber}/${column}/${letter}/${level}`,
      'get',
    );
  },

  getReadyQuantitiesForSellOrder: async (id: string): Promise<AxiosResponse<QuantityGetDto[]>> => {
    return request(`${API_URL}/inventory/get-ready-for-sell-order/${id}`, 'get');
  },

  getQuantitiesForSellOrder: async (id: string): Promise<AxiosResponse<QuantityGetDto[]>> => {
    return request(`${API_URL}/inventory/get-for-sell-order/${id}`, 'get');
  },

  markAsInvoiced: async (values: StockTransactionAddDto[]) => {
    const body = JSON.stringify(values);
    // console.log('body', body);
    return request(`${API_URL}/inventory/set-as-invoiced`, 'put', body);
  },

  sell: async (values) => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/sell`, 'post', body);
  },

  markAsNonInvoiced: async (values: StockTransactionAddDto[]): Promise<AxiosResponse<QuantityGetDto[]>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/set-as-not-invoiced`, 'put', body);
  },

  getAvailableForReservation: async (id: string): Promise<AxiosResponse<any>> => {
    return request(`${API_URL}/inventory/get-available-for-reservation/${id}`, 'get');
  },

  getReserved: async (id: string): Promise<AxiosResponse<ProductWithQuantitiesGetDto[]>> => {
    return request(`${API_URL}/inventory/get-reserved-for-order/${id}`, 'get');
  },

  getProforma: async (id: string): Promise<AxiosResponse<OrderProductWithReservedQuantityGetDto[]>> => {
    return request(`${API_URL}/orders/order-products/get-proforma-quantities-from-reservations/${id}`, 'get');
  },

  updateProforma: async (id: string): Promise<AxiosResponse<OrderWithProductsGetDto>> => {
    return request(`${API_URL}/orders/order-products/update-proforma-quantities-from-reservations/${id}`, 'put');
  },

  unsetProforma: async (id: string, quantity: number): Promise<AxiosResponse<OrderProductGetDTO>> => {
    return request(`${API_URL}/orders/order-products/unset-proforma-quantities/${id}/${quantity}`, 'put');
  },

  setManualValueProforma: async (id: string, quantity: number): Promise<AxiosResponse<OrderProductGetDTO>> => {
    return request(`${API_URL}/orders/order-products/set-proforma/${id}/${quantity}`, 'put');
  },

  moveAllFromLocation: async (fromLocation: LocationDto, toLocation: LocationDto): Promise<AxiosResponse> => {
    const object: ChangeLocationDto = {
      fromLocation,
      toLocation,
    };
    const body = JSON.stringify(object);
    return request(`${API_URL}/inventory/change-location`, 'put', body);
  },

  remove: async (values: RemoveStockTransactionAddDto): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/remove`, 'post', body);
  },

  removeFromLocationForOrder: async (id: string, location: LocationDto): Promise<AxiosResponse> => {
    const body = JSON.stringify(location);
    return request(`${API_URL}/inventory/remove-entire-location/${id}`, 'post', body);
  },

  removeAllFromLocation: async (location: LocationDto) => {
    const body = JSON.stringify(location);
    return request(`${API_URL}/inventory/remove-entire-location`, 'post', body);
  },

  edit: async (
    values: StockTransactionAddDto,
    transactionId: string,
  ): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/${transactionId}`, 'put', body);
  },

  editRemove: async (
    transactionId: string,
    values: RemoveStockTransactionAddDto,
  ): Promise<AxiosResponse<StockTransactionGetDto>> => {
    const body = JSON.stringify(values);
    return request(`${API_URL}/inventory/remove/${transactionId}`, 'put', body);
  },

  importMovementsFromExcel: async (id: string) => {
    return request(`${API_URL}/inventory/import-movements-from-excel/${id}`, 'post');
  },

  fixDates: async () => {
    return request(`${API_URL}/inventory/fix-dates`, 'post');
  },

  migrateRemove: async () => {
    return request(`${API_URL}/inventory/migrate-remove`, 'get');
  },

  reportOutgoing: async (dto: ReportRequestDto) => {
    const body = JSON.stringify(dto);
    return request(`${API_URL}/inventory/logs/outgoing/get`, 'post', body);
  },

  reportOutgoingExcel: async (dto: ReportRequestDto) => {
    const body = JSON.stringify(dto);
    return request(`${API_URL}/inventory/logs/outgoing/xls/get`, 'post', body, null, {
      responseType: 'arraybuffer',
    });
  },

  reportIncoming: async (dto: ReportRequestDto) => {
    const body = JSON.stringify(dto);
    return request(`${API_URL}/inventory/logs/incoming/get`, 'post', body);
  },

  reportIncomingExcel: async (dto: ReportRequestDto) => {
    const body = JSON.stringify(dto);
    return request(`${API_URL}/inventory/logs/incoming/xls/get`, 'post', body, null, {
      responseType: 'arraybuffer',
    });
  },
};
